import { Component, OnDestroy, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { initializeApp } from 'firebase/app';

import { GlobalService } from './core/services/global/global.service';

import { TokenService } from './core/services/token/token.service';

import { Router } from '@angular/router';

import { ShareExtension } from 'capacitor-share-extension';
import { Filesystem } from '@capacitor/filesystem';
import { Subscription } from 'rxjs';
import { TokenJWT } from './core/models/token/tokenJWT';
import { StorageService } from './core/services/storage/storage.service';
import { MessagingService } from './core/services/firebase/messaging/messaging.service';

import { Device } from '@capacitor/device';
import { NavController } from '@ionic/angular';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  token: TokenJWT | undefined;
  tokenSubscription$: Subscription = new Subscription();

  constructor(
    private globalService: GlobalService,
    private tokenService: TokenService,
    private storage: StorageService,
    private navCtrl: NavController,
    private router: Router,
    private messagingService: MessagingService
  ) {
    if (!Capacitor.isNativePlatform()) {
      initializeApp(environment.firebase);
    }
  }

  async ngOnInit() {
    try {
      const jwtString = await this.storage.get('jwtTokenBusiness');
      const token = new TokenJWT(jwtString);
      this.tokenService.setToken(token);
      this.globalService.setAccountType(token.payload.tipologia);
      if (!token.isValid()) {
        this.logOut();
      }
    } catch (error) {
      console.error(error);
      this.logOut();
    }

    this.tokenSubscription$ = this.tokenService
      .getToken()
      .subscribe(async (token) => {
        await this.checkSession(token);
      });
  }

  ngOnDestroy() {
    if (this.tokenSubscription$) this.tokenSubscription$.unsubscribe();
  }

  async checkSession(token: TokenJWT | undefined) {
    try {
      if (await this.isSessionAuthorized(token)) {
        await this.initNotifications(token!);
        await this.setupPluginTest();
      } else {
        this.logOut();
        this.navCtrl.navigateRoot(['/login'], {
          queryParams: { isSubscribed: false },
          replaceUrl: true
        });
      }
    } catch (error) {
      console.error(error);
      this.logOut();
      this.navCtrl.navigateRoot(['/login'], {replaceUrl: true});
    }
  }

  async initNotifications(token: TokenJWT) {
    try {
      await this.messagingService.init();
      await this.registerNotificationToken(token);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Controlla i permessi dell'account
   */
  async isSessionAuthorized(token: TokenJWT | undefined): Promise<Boolean> {
    if (token === undefined) {
      throw new Error('Utente non loggato o sessione non recuperabile');
    }

    const response = await fetch(
      environment.apiUrl + environment.apiVersion + '/accounts/state/read',
      {
        headers: { Authorization: token.toString() },
        method: 'GET',
      }
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        `Account compromesso o non autorizzato, redirect: ${data.description}`
      );
    }

    if (await this.isSubscribed(token)) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Ottiene l'abbonamento dell'utente e verifica se è attivo
   */
  async isSubscribed(token: TokenJWT): Promise<boolean> {
    try {
      const response = await fetch(
        environment.apiUrl +
          environment.apiVersion +
          '/accounts/subscriptions/read',
        {
          headers: { Authorization: token.toString() },
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`${data.description}`);
      }

      const abbonamento = data.body;
      if (abbonamento && abbonamento.length > 0) {
        const subscription = abbonamento[0];

        //calcolo la data di fine dell'abbonamento
        const startDate = new Date(abbonamento[0].dataOraInizio.split(' ')[0]);
        const expirationDate = new Date(
          startDate.getTime() + subscription.durata * 1000 * 60 * 60 * 24
        );
        const currentDate = new Date();

        return currentDate < expirationDate && subscription.idStato != 3;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return false;
    }
  }

  async logOut() {
    this.messagingService.removeToken();
    await this.removeNotificationToken();

    await this.storage.remove('jwtTokenBusiness');
    this.globalService.clearAccountType();
  }

  async registerNotificationToken(token: TokenJWT) {
    // Da dove prenderò le informazioni del dispositivo
    const id = await Device.getId();
    const info = await Device.getInfo();
    const params = JSON.stringify({
      Identificativo: id.identifier,
      SistemaOperativo: info.platform,
      Modello: info.model,
      Nome: info.name,
      TokenNotifiche: await this.messagingService.getToken(),
    });
    fetch(
      environment.apiUrl +
        environment.apiVersion +
        '/notifications/device/create',
      {
        headers: { Authorization: token.toString() },
        body: params,
        method: 'POST',
      }
    );
  }

  async removeNotificationToken() {
    const params = JSON.stringify({
      TokenNotifiche: await this.messagingService.getToken(),
    });
    fetch(
      environment.apiUrl +
        environment.apiVersion +
        '/notifications/device/delete',
      {
        body: params,
        method: 'POST',
      }
    );
  }

  //#region trasferimento file ricetta
  /**
   * funzione per inizializzare plugin ShareExtension
   */
  async setupPluginTest() {
    // run this as part of the app launch
    if (Capacitor.isPluginAvailable('ShareExtension')) {
      window.addEventListener('sendIntentReceived', () => {
        this.checkIntent();
      });
      this.checkIntent();
    }
  }
  /**
   * Controlla l'intent ricevuto e lo elabora se è valido
   */
  async checkIntent() {
    try {
      const result: any = await ShareExtension.checkSendIntentReceived();
      if (result && result.payload && result.payload.length) {
        this.readFilePath(
          result['payload'][0]['url'],
          result['payload'][0]['title']
        );
      }
    } catch (err) {
      console.log(err);
    }
  }

  /* in Android, call finish when done processing the Intent
  Non so dove usarlo e se è necessario
  await ShareExtension.finish()
  */

  readFilePath = async (filePath: string, filename: string) => {
    // Here's an example of reading a file with a full file path. Use this to
    // read binary data (base64 encoded) from plugins that return File URIs, such as
    // the Camera.
    const contents = await Filesystem.readFile({
      path: filePath,
    });
    const queryParams = {
      fileName: filename,
      PDF: contents['data'],
    };
    this.router.navigate(['/tabs/ricette'], { queryParams: queryParams });
  };
  //#endregion trasferimento file
}
